import { useState } from 'react';

interface OrderFormProps {
  customization: {
    shirtColor: string;
    printLocation: string;
    elements: Array<{
      type: string;
      content: string;
      style?: Record<string, unknown>;
    }>;
    size: string;
    quantity: number;
  };
  totalPrice: number;
  onClose: () => void;
}

const FORM_URL = 'https://readdy.ai/api/form/d6jousrcs59vsrrrk6cg';

export function OrderForm({ customization, totalPrice, onClose }: OrderFormProps) {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!customization.size) {
      setStatus('error');
      setMessage('Please select a size before ordering.');
      return;
    }

    setStatus('loading');

    const form = e.currentTarget as HTMLFormElement;
    const formData = new URLSearchParams();
    formData.append('shirt_color', customization.shirtColor);
    formData.append('print_location', customization.printLocation);
    formData.append('design_elements', String(customization.elements.length));
    formData.append('size', customization.size);
    formData.append('quantity', String(customization.quantity));
    formData.append('total_price', `$${totalPrice.toFixed(2)}`);

    const emailEl = form.elements.namedItem('email') as HTMLInputElement;
    const nameEl = form.elements.namedItem('customer_name') as HTMLInputElement;
    const notesEl = form.elements.namedItem('notes') as HTMLTextAreaElement;

    if (emailEl) formData.append('email', emailEl.value);
    if (nameEl) formData.append('customer_name', nameEl.value);
    if (notesEl) formData.append('notes', notesEl.value);

    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });

      if (res.ok) {
        setStatus('success');
        setMessage('🎉 Order submitted! We\'ll be in touch shortly to confirm your custom shirt.');
      } else {
        setStatus('error');
        setMessage('Something went wrong. Please try again.');
      }
    } catch {
      setStatus('error');
      setMessage('Network error. Please try again.');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <h3 className="text-lg font-black text-gray-900 uppercase tracking-wider">
            Complete Your Order
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-500 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <form
          id="custom-shirt-order-form"
          data-readdy-form
          onSubmit={handleSubmit}
          className="p-6 space-y-5"
        >
          {/* Customer Name */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1 uppercase tracking-wider">
              Your Name
            </label>
            <input
              type="text"
              name="customer_name"
              required
              placeholder="Jane Smith"
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-red-900 focus:outline-none text-sm"
            />
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1 uppercase tracking-wider">
              Email Address
            </label>
            <input
              type="email"
              name="email"
              required
              placeholder="you@example.com"
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-red-900 focus:outline-none text-sm"
            />
          </div>

          {/* Special Notes */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1 uppercase tracking-wider">
              Special Notes <span className="text-gray-400 font-normal normal-case">(optional)</span>
            </label>
            <textarea
              name="notes"
              rows={3}
              maxLength={500}
              placeholder="Any special requests or instructions..."
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-red-900 focus:outline-none text-sm resize-none"
            />
          </div>

          {/* Order Summary */}
          <div className="bg-gray-50 rounded-xl p-4 space-y-2">
            <div className="flex justify-between text-sm text-gray-600">
              <span>Shirt Color</span>
              <span className="font-semibold">{customization.shirtColor}</span>
            </div>
            <div className="flex justify-between text-sm text-gray-600">
              <span>Size</span>
              <span className="font-semibold">{customization.size}</span>
            </div>
            <div className="flex justify-between text-sm text-gray-600">
              <span>Quantity</span>
              <span className="font-semibold">x{customization.quantity}</span>
            </div>
            <div className="border-t border-gray-200 pt-2 flex justify-between font-black text-gray-900 text-lg">
              <span>Total</span>
              <span className="text-red-900">${totalPrice.toFixed(2)}</span>
            </div>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={status === 'loading'}
            className="w-full py-4 bg-red-900 text-white font-black text-base rounded-xl hover:bg-red-800 transition-colors disabled:opacity-60 whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
          >
            {status === 'loading' ? (
              <>
                <i className="ri-loader-4-line animate-spin"></i>
                Submitting...
              </>
            ) : (
              <>
                <i className="ri-shopping-bag-line"></i>
                Place Custom Order — ${totalPrice.toFixed(2)}
              </>
            )}
          </button>

          {status === 'success' && (
            <div className="p-4 bg-green-50 border border-green-200 text-green-800 rounded-xl text-sm font-medium text-center">
              {message}
            </div>
          )}
          {status === 'error' && (
            <div className="p-4 bg-red-50 border border-red-200 text-red-800 rounded-xl text-sm font-medium text-center">
              {message}
            </div>
          )}

          <p className="text-xs text-gray-400 text-center">
            <i className="ri-shield-check-line mr-1"></i>
            Secure order · Ships in 5–7 business days
          </p>
        </form>
      </div>
    </div>
  );
}
